import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Alert, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
} from '@mui/material';
import { getLeadershipHealth, STATUS_TONE } from '../../../services/sprintHealthService';
import { Pill, Spark, Dots, Caption, SEG, headCellSx } from './shared';
import AiNotesCard from './AiNotesCard';

const Kr = ({ label, value, target, ok }) => (
  <Paper sx={{ p: 2, px: 2.25, borderRadius: '12px' }}>
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: '1.75rem', fontWeight: 300, my: 0.5, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
      {ok != null && <Pill tone={ok ? 'ok' : 'warn'}>{ok ? 'On track' : 'Behind'}</Pill>}
      <Caption>{target}</Caption>
    </Box>
  </Paper>
);

const pctOrDash = (kr) => (kr.value == null ? '—' : `${kr.value}%`);
const onTrack = (kr) => (kr.value == null ? null : kr.value >= kr.target);
const targetText = (kr, window) => (kr.value == null ? `No data yet · target ${kr.target}%` : `Target ${kr.target}% · ${window}`);

const Cell = ({ spark, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {spark}
    <Box component="b" sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
  </Box>
);

const LeadershipHealthView = ({ sprint, onOpenTeam, toggle }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sprint) return;
    let alive = true;
    setLoading(true);
    setError(null);
    getLeadershipHealth(sprint)
      .then((res) => { if (alive) setData(res.data); })
      .catch((err) => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sprint]);

  const head = (text) => (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap', pb: 0.25, color: '#4b5a52', fontSize: 13 }}>
      {toggle}
      {text && <span>{text}</span>}
    </Box>
  );
  if (loading && !data) return <>{head()}<Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box></>;
  if (error) return <>{head()}<Alert severity="error" sx={{ mt: 2 }}>{error}</Alert></>;
  if (!data) return head();
  const k = data.krs;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {head(`All teams as of ${data.sprintName}. Flags use trends over the last 3 sprints. Select a team to see its review.`)}

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(5, minmax(0,1fr))' } }}>
        <Kr label="KR1 · Sprint goals met" value={pctOrDash(k.goalsMet)} target={targetText(k.goalsMet, 'rolling 6 sprints')} ok={onTrack(k.goalsMet)} />
        <Kr label="KR2 · Commitments moving" value={pctOrDash(k.commitmentsMoving)} target={targetText(k.commitmentsMoving, 'this sprint')} ok={onTrack(k.commitmentsMoving)} />
        <Kr label="KR3 · Effort on commitments" value={pctOrDash(k.onCommitments)} target={targetText(k.onCommitments, 'this sprint')} ok={onTrack(k.onCommitments)} />
        <Kr label="KR4 · Retro actions closed" value={pctOrDash(k.retroClosed)} target={targetText(k.retroClosed, 'rolling 6 sprints')} ok={onTrack(k.retroClosed)} />
        <Kr label="KR5 · Cycle time P85" value={k.cycleP85.value != null ? `${k.cycleP85.value}d` : '—'} target={k.cycleP85.was != null ? `Was ${k.cycleP85.was}d six sprints ago · aim: trending down` : 'Aim: trending down'} ok={k.cycleP85.value != null && k.cycleP85.was != null ? k.cycleP85.value <= k.cycleP85.was : null} />
      </Box>

      <AiNotesCard
        title="AI notes across teams"
        desc="Patterns across all teams, drafted from the same facts as the table below. They appear at the top of every sprint email."
        notes={data.portfolioNotes}
        editable={false}
        emptyHint="No cross-team notes yet. They are drafted on the last day of the sprint."
      />

      <TableContainer component={Paper} sx={{ borderRadius: '12px' }}>
        <Table size="small" sx={{ minWidth: 1080 }}>
          <TableHead>
            <TableRow>
              {['Team', 'Health', 'Goal met (6)', 'Delivery moved (6)', 'Effort on commitments', 'Forecast reliability', 'Cycle P85', 'Escaped defects', 'Pulse', 'Commitments'].map((h) => (
                <TableCell key={h} sx={headCellSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.teams.map((r) => {
              if (!r.health) {
                return (
                  <TableRow key={r.team}>
                    <TableCell><b>{r.displayName}</b></TableCell>
                    <TableCell colSpan={9}><Caption>No sprint data for this sprint. Sync the sprint in Admin → Sprint Management.</Caption></TableCell>
                  </TableRow>
                );
              }
              const h = r.health.history;
              const six = h.slice(-6);
              const c = r.health.current;
              const active = c.commitments.filter((m) => m.active);
              const delivered = active.filter((m) => m.afterPct >= 100).length;
              return (
                <TableRow
                  key={r.team}
                  hover
                  tabIndex={0}
                  onClick={() => onOpenTeam(r.team)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onOpenTeam(r.team); }}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell><b>{r.displayName}</b></TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Pill tone={STATUS_TONE[r.health.status.code]}>{r.health.status.label}</Pill>
                    {r.health.flags.length > 0 && <Caption sx={{ mt: 0.5 }}>{r.health.flags.map((f) => f.text).join('. ')}</Caption>}
                  </TableCell>
                  <TableCell><Dots items={six.map((m) => ({ title: m.shortName, tone: m.goal.outcome === 'met' ? 'ok' : m.goal.outcome === 'not_met' ? 'bad' : m.goal.outcome === 'partly' ? 'warn' : 'none' }))} /></TableCell>
                  <TableCell><Dots items={six.map((m) => ({ title: `${m.shortName}: ${m.delivery ? `${m.delivery.moved} of ${m.delivery.total} moved` : 'no data'}`, tone: m.delivery?.moved > 0 ? 'ok' : 'off' }))} /></TableCell>
                  <TableCell><Cell spark={<Spark values={h.map((m) => m.shares.onCommitments)} min={0} max={100} />} value={`${c.shares.onCommitments}%`} /></TableCell>
                  <TableCell><Cell spark={<Spark values={h.map((m) => m.forecast.reliability)} min={0} max={100} />} value={c.forecast.reliability != null ? `${c.forecast.reliability}%` : '—'} /></TableCell>
                  <TableCell><Cell spark={<Spark values={h.map((m) => m.cycle.p85)} color={SEG.quarterly} />} value={c.cycle.p85 != null ? `${c.cycle.p85}d` : '—'} /></TableCell>
                  <TableCell><Cell spark={<Spark values={h.map((m) => m.escapedDefects)} color={SEG.unlinked} min={0} />} value={c.escapedDefects} /></TableCell>
                  <TableCell><Cell spark={<Spark values={h.map((m) => m.pulse?.avg ?? null)} min={1} max={5} />} value={c.pulse ? c.pulse.avg : '—'} /></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{active.length ? `${delivered} delivered · ${active.length - delivered} open` : '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Caption>Teams are listed alphabetically, never ranked. Velocity, story points and individual names are not shown here.</Caption>
    </Box>
  );
};

export default LeadershipHealthView;
